import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Reply, ReplyAll, Forward, Trash2, Star, AlertTriangle, MailOpen, Mail,
  Paperclip, Download,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { formatDateTime } from '../../../utils/appDateTime';
import { downloadCrmMailAttachment } from '../../../utils/crmMailDownload';
import { useToast } from '../../../hooks/use-toast';
import CrmMailAttachmentPreview from './CrmMailAttachmentPreview';

function formatAddress(addr) {
  if (!addr) return '—';
  return addr.name ? `${addr.name} <${addr.email}>` : addr.email;
}

function formatAddressList(list) {
  if (!list?.length) return '—';
  return list.map(formatAddress).join(', ');
}

export default function CrmMailPreview({
  message,
  thread,
  loading,
  onReply,
  onReplyAll,
  onForward,
  onAction,
}) {
  const [previewAtt, setPreviewAtt] = useState(null);
  const { toast } = useToast();

  const handleDownloadAttachment = async (att) => {
    try {
      await downloadCrmMailAttachment(att.id, att.filename);
    } catch (e) {
      toast({
        title: 'Download failed',
        description: e?.message || 'Could not download attachment',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground">Loading…</div>;
  }

  if (!message) {
    return null;
  }

  const safeHtml = DOMPurify.sanitize(message.body_html || message.snippet || '', {
    ADD_ATTR: ['target'],
  });

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h2 className="text-lg font-semibold flex-1">{message.subject || '(no subject)'}</h2>
          <div className="flex flex-wrap gap-1">
            <Button variant="ghost" size="icon" onClick={onReply} title="Reply (r)">
              <Reply className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onReplyAll} title="Reply all">
              <ReplyAll className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onForward} title="Forward">
              <Forward className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onAction(message.is_starred ? 'unstar' : 'star')}
              title="Star"
            >
              <Star className={`w-4 h-4 ${message.is_starred ? 'fill-amber-500 text-amber-500' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onAction(message.is_important ? 'unimportant' : 'important')}
              title="Important"
            >
              <AlertTriangle className={`w-4 h-4 ${message.is_important ? 'text-orange-500' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onAction(message.is_read ? 'unread' : 'read')}
              title="Read/unread"
            >
              {message.is_read ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onAction('delete')} title="Delete (e)">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="text-sm space-y-1 text-muted-foreground">
          <p><span className="font-medium text-foreground">From:</span> {formatAddress(message.from)}</p>
          <p><span className="font-medium text-foreground">To:</span> {formatAddressList(message.to)}</p>
          {message.cc?.length > 0 && (
            <p><span className="font-medium text-foreground">Cc:</span> {formatAddressList(message.cc)}</p>
          )}
          <p className="text-xs">{formatDateTime(message.received_at || message.sent_at)}</p>
        </div>
        {message.attachments?.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {message.attachments.map((att) => (
              <div key={att.id} className="flex items-center gap-1 border rounded-lg px-2 py-1 text-xs">
                <Paperclip className="w-3 h-3" />
                <button
                  type="button"
                  className="hover:underline"
                  onClick={() => (att.previewable ? setPreviewAtt(att) : handleDownloadAttachment(att))}
                >
                  {att.filename}
                </button>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => handleDownloadAttachment(att)}
                  aria-label={`Download ${att.filename}`}
                >
                  <Download className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {thread.length > 1 ? (
          <div className="space-y-4">
            {thread.map((t) => (
              <div key={t.id} className="border-l-2 border-muted pl-4">
                <p className="text-xs text-muted-foreground mb-1">
                  {formatAddress(t.from)} · {formatDateTime(t.received_at || t.sent_at)}
                </p>
                <div
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t.body_html || t.snippet || '') }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div
            className="prose prose-sm dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        )}
      </div>

      {previewAtt && (
        <CrmMailAttachmentPreview
          attachment={previewAtt}
          onClose={() => setPreviewAtt(null)}
        />
      )}
    </div>
  );
}
